import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { View, Pressable, StyleSheet, TextInput, FlatList, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useChatStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const MAX_RESULTS = 50;

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const then = new Date(dateStr).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months}mo`;
}

const ChatListItem = React.memo(function ChatListItem({ id, title, time, onPress, colors }) {
  const handlePress = useCallback(() => onPress(id), [onPress, id]);
  const displayTitle = title || 'Untitled Chat';
  const displayTime = formatRelativeTime(time);

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={`Open ${displayTitle}`}
      style={({ hovered }) => [
        styles.chatItem,
        hovered && { backgroundColor: colors.solid_color_secondary },
      ]}
    >
      <View style={styles.chatItemRow}>
        <Icon name="message" size={15} color={colors.text_color_secondary} />
        <Text size="s" color="primary" numberOfLines={1} style={styles.chatItemTitle}>
          {displayTitle}
        </Text>
        {displayTime ? (
          <Text size="xs" color="tertiary" style={styles.chatItemTime}>
            {displayTime}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
});

// Project the store's chat list down to the minimal shape we need. Using
// `useShallow` keeps this hook from re-rendering when unrelated chat fields
// change (e.g. a single message arriving on an unrelated chat) — only the
// fields we actually display will trigger an update.
const chatSummariesSelector = (state) => {
  const out = [];
  for (const c of state.chats) {
    if (c.isOwner === false) continue;
    if (c.flags?.isTemporary) continue;
    if (c.flags?.isToolRun === true && !c.flags?.isToolPromotedToChat) continue;
    out.push({
      id: c.id,
      title: c.title || '',
      time: c.lastMessageAt || c.updatedAt || c.createdAt || null,
    });
  }
  return out;
};

function summariesEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id || a[i].title !== b[i].title || a[i].time !== b[i].time) return false;
  }
  return true;
}

export default function DeckChatPicker({ onSelectChat, onNewConversation }) {
  const { colors } = useTheme();
  const [query, setQuery] = useState('');
  const [summaries, setSummaries] = useState(() => chatSummariesSelector(useChatStore.getState()));
  const prevRef = useRef(summaries);

  useEffect(() => {
    return useChatStore.subscribe((state) => {
      const next = chatSummariesSelector(state);
      if (!summariesEqual(prevRef.current, next)) {
        prevRef.current = next;
        setSummaries(next);
      }
    });
  }, []);

  const filteredChats = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const filtered = trimmed
      ? summaries.filter(c => c.title.toLowerCase().includes(trimmed))
      : summaries;

    // Sort newest-first by parsed timestamp; missing dates sink to the bottom.
    const sorted = filtered.slice().sort((a, b) => {
      const aTime = a.time ? new Date(a.time).getTime() : 0;
      const bTime = b.time ? new Date(b.time).getTime() : 0;
      return bTime - aTime;
    });

    return sorted.slice(0, MAX_RESULTS);
  }, [summaries, query]);

  const handleSelect = useCallback((chatId) => {
    onSelectChat(chatId);
  }, [onSelectChat]);

  const handleClearQuery = useCallback(() => setQuery(''), []);

  const renderItem = useCallback(({ item }) => (
    <ChatListItem id={item.id} title={item.title} time={item.time} onPress={handleSelect} colors={colors} />
  ), [handleSelect, colors]);

  const keyExtractor = useCallback((item) => item.id, []);

  const isSearching = query.trim().length > 0;

  return (
    <View style={[styles.container, { backgroundColor: colors.solid_color_primary }]}>
      <View style={styles.intro}>
        <Text size="l" weight="semibold">What’s next?</Text>
        <Text size="s" color="secondary" style={styles.introCopy}>Start something new, or pick up where you left off.</Text>
      </View>
      <View style={styles.newConvoContainer}>
        <Pressable
          onPress={onNewConversation}
          accessibilityRole="button"
          accessibilityLabel="New conversation"
          style={({ hovered }) => [styles.newConvoButton, { backgroundColor: hovered ? colors.solid_color_tertiary : colors.solid_color_secondary }]}
        >
          <Icon name="pen-to-square" size={16} color={colors.text_color_primary} />
          <Text size="s" weight="medium">New conversation</Text>
          <Icon name="arrow-right" size={13} color={colors.text_color_secondary} style={{ marginLeft: 'auto' }} />
        </Pressable>
      </View>
      <View style={styles.searchContainer}>
        <View style={[styles.searchBox, { borderColor: colors.border_color_primary, backgroundColor: colors.solid_color_primary }]}>
          <Icon name="magnifying-glass" size={14} color={colors.text_color_tertiary} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search conversations..."
            placeholderTextColor={colors.text_color_tertiary}
            style={[styles.searchInput, { color: colors.text_color_primary }]}
            accessibilityLabel="Search conversations"
          />
          {query.length > 0 && (
            <Pressable onPress={handleClearQuery} hitSlop={8} accessibilityRole="button" accessibilityLabel="Clear search">
              <Icon name="xmark" size={12} color={colors.text_color_tertiary} />
            </Pressable>
          )}
        </View>
      </View>

      <View style={styles.sectionHeader}>
        <Text size="xs" weight="medium" color="secondary">
          {isSearching ? 'Search results' : 'Recent conversations'}
        </Text>
      </View>

      <FlatList
        data={filteredChats}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text size="s" color="tertiary" style={styles.emptyText}>
              {isSearching ? 'No conversations found' : 'No conversations yet'}
            </Text>
          </View>
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  intro: {
    padding: 24,
    paddingBottom: 20,
  },
  introCopy: {
    marginTop: 8,
    lineHeight: 22,
  },
  searchContainer: {
    padding: 20,
    paddingBottom: 0,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    minHeight: 40,
    borderWidth: 1,
    borderRadius: 10,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 10,
    ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
  },
  newConvoContainer: {
    paddingHorizontal: 20,
    paddingBottom: 0,
  },
  newConvoButton: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 48,
    paddingHorizontal: 16,
    borderRadius: 12,
  },
  sectionHeader: {
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 12,
    paddingBottom: 16,
  },
  chatItem: {
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 10,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  chatItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  chatItemTitle: {
    flex: 1,
  },
  chatItemTime: {
    flexShrink: 0,
  },
  emptyContainer: {
    padding: 20,
    alignItems: 'center',
  },
  emptyText: {
    textAlign: 'center',
  },
});
